/**
 * Server functions + shared logic for plan subscriptions and prepaid data
 * purchases. Gateway-agnostic: it resolves WHAT the customer may buy and at what
 * price (entitlement, the org's plan, the per-cycle top-up cap, the redirect
 * origin) and then hands the hosted checkout to whichever gateway the registry
 * picks. No payment SDK is imported here.
 *
 * Flow: the customer picks an amount of GB → `createPurchaseDataCheckout`
 * prices it against the org's own plan and asks the gateway for a hosted
 * checkout URL → the client redirects there → on payment the gateway's webhook
 * calls `creditOrgPurchaseData` (purchase-data.server.ts) to add capacity to the
 * org wallet and record a `purchaseData` ledger entry.
 *
 * Not marked `server-only`: it exports `createServerFn`s the client imports as
 * RPC stubs; the compiler strips the handler bodies (and the server-only imports
 * they use — db, auth, the gateway registry) from the client bundle. Crediting
 * lives in the server-only `purchase-data.server.ts` (a plain export touching db
 * would NOT be stripped and would leak db into the client bundle).
 */
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import {
  and,
  asc,
  count,
  desc,
  eq,
  gte,
  inArray,
  isNull,
  ne,
  notLike,
  or,
  sql,
} from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import {
  ledgerEntries,
  organizationBilling,
  prepaidTerms,
  trialClaims,
} from '@/db/schema'
import {
  type BillingMode,
  BYTES_PER_GB,
  blocksNewPlanPurchase,
  gatewayOwnsSubscription,
  isEntitled,
  microUsdToCents,
  type PaymentRail,
  type PricingPlan,
  planPerGbMicroUsd,
  priceForGb,
  splitCycleUsage,
  usedThisCycleBytes,
} from '@/lib/billing/billing'
import {
  COUPON_CODE_RE,
  COUPON_TARGETS,
  type CouponTarget,
  describeCouponAmount,
  describeCouponDuration,
} from '@/lib/billing/coupons'
import {
  resolveCouponForCheckout,
  resolveOptionalCoupon,
} from '@/lib/billing/coupons.server'
import {
  gatewayFor,
  gatewayForFundingSource,
  getGateway,
  PURCHASING_DISABLED,
  purchaseGatewayForOrg,
} from '@/lib/billing/gateways/registry'
import type { PaymentGateway } from '@/lib/billing/gateways/types'
import {
  type LedgerKind,
  type LedgerPage,
  type LedgerSort,
  ledgerRowsToCsv,
  normalizeLedgerKinds,
  normalizeLedgerPage,
  normalizeLedgerSort,
} from '@/lib/billing/ledger'
import { expirePlanBucket } from '@/lib/billing/plan-cycle.server'
import {
  getActivePlans,
  getPlanForOrg,
  getPlansForOrg,
} from '@/lib/billing/pricing.server'
import {
  CARD_TRIAL_DAYS,
  TRIAL_ALLOWANCE_BYTES,
  TRIAL_PLAN_SLUG,
  type TrialKind,
  type TrialOffer,
} from '@/lib/billing/trial'
import {
  getTrialOffer,
  trialIneligibilityReason,
} from '@/lib/billing/trial.server'
import { PublicError } from '@/lib/errors'
import { requireOrgAdmin, requireOrgMember } from '@/lib/org/require-org.server'
import { env } from '@/lib/server/env'
import { emitBusinessEvent } from '@/lib/telemetry/business.server'
import { captureMessage } from '@/lib/telemetry/telemetry'
import { parseInput } from '@/lib/validation'

/** Additional-data top-ups are whole GB, at least this many. The upper bound is
 *  the org's plan `topUpCapGb`, resolved per-request in the checkout handler. */
const MIN_PURCHASE_GB = 1

/** Plan slug as the client may send it. Shared by all three checkouts so the
 *  card and crypto paths cannot drift on what a valid slug is. */
const planSlugInput = z
  .string()
  .trim()
  .min(1, 'A plan is required')
  .max(32, 'A plan is required')
  .regex(/^[a-z0-9-]+$/, 'A plan is required')

const termMonthsInput = z
  .number()
  .int('Choose a whole number of months')
  .min(1, 'Choose at least one month')
  .max(12, 'Choose up to 12 months')

/**
 * An optional coupon code. Normalized to upper case here so a code typed in
 * lower case matches, and shaped against the same pattern the admin editor
 * enforces — an input that could never be a code is refused before it reaches
 * a database lookup. Empty/absent means no coupon, which every checkout treats
 * as the ordinary case rather than an error.
 */
const couponCodeInput = z
  .string()
  .trim()
  .toUpperCase()
  .max(32, "That code isn't valid.")
  .regex(COUPON_CODE_RE, "That code isn't valid.")
  .nullish()
  .transform((v) => v ?? null)

// ---------------------------------------------------------------------------
// Guard
// ---------------------------------------------------------------------------

/**
 * Total top-up bytes consumed SINCE the current billing cycle began, NET OF
 * REFUNDS — the window is anchored on the org's stored `cycleStartAt` (set by
 * cycle-reset grants only, NOT mid-cycle upgrades, so an upgrade doesn't refill
 * the top-up allowance). Sums `purchaseData` (positive) AND TOP-UP `refund`
 * (negative) rows in the window, so refunded top-ups (operator refunds and the
 * system's own over-cap auto-refunds) don't permanently consume the cycle's
 * allowance. SUBSCRIPTION-invoice clawbacks/disputes are `refund` rows too
 * (keyed `sub-clawback:%`) but are NOT top-up refunds, so they're excluded — a
 * subscription clawback must not free top-up cap allowance. Clamped at 0.
 * Returns 0 when the org has never had a cycle (`cycleStartAt` null — no cycle
 * to count against yet). Mirrors `topUpUsedThisCycleBytesTx` in
 * purchase-data.server.ts.
 */
async function topUpUsedThisCycleBytes(
  organizationId: string,
  cycleStartAt: Date | null,
): Promise<number> {
  if (!cycleStartAt) return 0
  const [sum] = await db
    .select({
      bytes: sql<number>`coalesce(sum(${ledgerEntries.amountBytes}), 0)`,
    })
    .from(ledgerEntries)
    .where(
      and(
        eq(ledgerEntries.organizationId, organizationId),
        inArray(ledgerEntries.kind, ['purchaseData', 'refund']),
        // Exclude subscription-invoice clawbacks/disputes (keyed
        // `sub-clawback:%`) — they're `refund` rows but not top-up refunds, so
        // they must not net against the top-up cap. Top-up rows never carry
        // this prefix.
        or(
          isNull(ledgerEntries.externalRef),
          notLike(ledgerEntries.externalRef, 'sub-clawback:%'),
        ),
        gte(ledgerEntries.createdAt, cycleStartAt),
      ),
    )
  return Math.max(0, Number(sum?.bytes ?? 0))
}

/**
 * Best app base URL for gateway redirect URLs. Pinned to
 * `env.BETTER_AUTH_URL`, or the request `Origin` header ONLY when it exactly
 * matches an allowlisted origin (BETTER_AUTH_URL + BETTER_AUTH_TRUSTED_ORIGINS).
 * The raw `Origin` header is attacker-controllable, so it must never flow into
 * `success_url`/`cancel_url`/`return_url` unvalidated — that would let a forged
 * header redirect a paying customer to an attacker's site post-checkout.
 */
function appOrigin(): string {
  const canonical = env.BETTER_AUTH_URL
  let origin: string | null = null
  try {
    origin = getRequest().headers.get('origin')
  } catch {
    // No request context (e.g. background/maintenance call) — use the canonical.
  }
  if (!origin) return canonical
  const allowed = new Set([canonical, ...env.BETTER_AUTH_TRUSTED_ORIGINS])
  return allowed.has(origin) ? origin : canonical
}

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

/**
 * Start a hosted checkout to add `gb` gigabytes of data to the active org's
 * wallet, and return its URL for the client to redirect to.
 *
 * Additional data is an add-on to an ACTIVE subscription: the org must be
 * entitled, the amount is capped by the org's plan `topUpCapGb`, and it prices
 * at the org's own plan per-GB rate (not the base top-up rate). Every one of
 * those checks is gateway-independent and stays here; only the hosted page
 * itself comes from the gateway.
 */
export const createPurchaseDataCheckout = createServerFn({ method: 'POST' })
  .validator((d: { gb: number; couponCode?: string | null }) => {
    // The upper bound depends on the org's plan, resolved in the handler; here
    // we only coerce to a whole GB (the [MIN, cap] check happens below).
    const gb = Math.floor(Number(d.gb))
    if (!Number.isFinite(gb)) {
      throw new PublicError('Amount must be a whole number of GB')
    }
    return { gb, couponCode: parseInput(couponCodeInput, d.couponCode) }
  })
  .handler(async ({ data }): Promise<{ url: string }> => {
    // Money operations are owner/admin-only (a plain member can still use
    // credentials, but not spend).
    const { organizationId, email } = await requireOrgAdmin()
    const { gb } = data

    // Additional data requires an active subscription — resolve the org's plan
    // (entitlement + cap + rate) before anything else.
    await db
      .insert(organizationBilling)
      .values({ organizationId })
      .onConflictDoNothing()
    const [row] = await db
      .select({
        planSlug: organizationBilling.planSlug,
        subscriptionRef: organizationBilling.subscriptionRef,
        paymentRail: organizationBilling.paymentRail,
        accountManaged: organizationBilling.accountManaged,
        planPeriodEnd: organizationBilling.planPeriodEnd,
        cycleStartAt: organizationBilling.cycleStartAt,
        subscriptionStatus: organizationBilling.subscriptionStatus,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, organizationId))
      .limit(1)
    // The trial is the taste, not the tap: more data means picking a plan.
    if (row?.subscriptionStatus === 'trialing') {
      throw new PublicError(
        'Additional data is not available during the free trial. Pick a plan to get more data.',
      )
    }
    const entitled = row
      ? isEntitled(
          {
            subscriptionRef: row.subscriptionRef,
            planPeriodEnd: row.planPeriodEnd,
          },
          new Date(),
        )
      : false
    // Org-aware: MUST resolve the same row getOrgPlanStatus advertised on the
    // card. Resolving from the public catalog here meant an org on a negotiated
    // plan was either refused outright ("subscribe to a plan", to a paying
    // customer) or quoted the private rate/cap and then charged/enforced the
    // public one.
    const plan = await getPlanForOrg(organizationId, row?.planSlug ?? null)
    if (!entitled || !plan) {
      throw new PublicError(
        'Subscribe to a plan before purchasing additional data.',
      )
    }

    // Which rail serves this org, if any. Null for an account-managed
    // (invoiced/comped) org — it gets one consolidated invoice, so we must not
    // open a second self-serve billing relationship alongside it — and when no
    // live rail can sell a top-up at all.
    const gateway = purchaseGatewayForOrg({
      paymentRail: row?.paymentRail ?? 'none',
      accountManaged: row?.accountManaged ?? false,
      capability: 'topUps',
    })
    if (!gateway) {
      throw new PublicError(
        row?.accountManaged
          ? 'Your organization is billed by invoice. Contact your account team to add data.'
          : PURCHASING_DISABLED,
      )
    }

    if (gb < MIN_PURCHASE_GB) {
      throw new PublicError(
        `Additional data must be at least ${MIN_PURCHASE_GB} GB`,
      )
    }

    // Per-CYCLE cap: the cap limits total top-ups bought since this cycle began,
    // not each purchase. The current cycle started at the org's most recent
    // plan grant (a subscription_create/cycle reset, or an upgrade), so sum the
    // `purchaseData` bytes credited since then and refuse if this purchase would
    // push the cycle total over the plan's cap. (This is a checkout-time guard;
    // two near-simultaneous checkouts could still both pass — an accepted narrow
    // race, since every GB is still paid for and it's a sanity limit, not a
    // security boundary.)
    const cap = plan.topUpCapGb
    const usedThisCycleGb =
      (await topUpUsedThisCycleBytes(
        organizationId,
        row?.cycleStartAt ?? null,
      )) / BYTES_PER_GB
    const remainingGb = Math.max(0, cap - usedThisCycleGb)
    if (gb > remainingGb) {
      throw new PublicError(
        remainingGb <= 0
          ? `You've reached your plan's additional-data limit of ${cap} GB for this billing cycle.`
          : `Your plan allows up to ${cap} GB of additional data per billing cycle. You have ${remainingGb} GB remaining this cycle.`,
      )
    }

    // Price at the ORG'S plan per-GB rate. The rate rides along to the gateway
    // so the webhook credits at the price the customer saw, even if the catalog
    // changes before settlement.
    const rate = planPerGbMicroUsd(plan)
    const priceMicros = priceForGb(gb, rate)
    // A catalog revision whose per-GB rate doesn't divide into whole cents
    // would make the gateway's own unit conversion throw a raw assertion
    // mid-checkout. Check it here and turn it into an actionable, plan-named
    // error + SigNoz alarm so ops notice the mis-priced revision instead of
    // customers hitting a 500.
    try {
      microUsdToCents(priceMicros)
    } catch {
      captureMessage('[billing] plan rate does not divide into whole cents', {
        level: 'error',
        extra: { planSlug: plan.slug, gb, rate, priceMicros },
      })
      throw new PublicError(
        `The "${plan.name}" plan is temporarily unavailable for additional data (a pricing configuration error). Please contact support.`,
      )
    }

    // A coupon on a top-up discounts the RATE, not just the total. Settlement
    // credits `paid / stampedRate` bytes, so stamping the catalog rate against
    // a discounted payment would hand the customer the GB their money bought
    // at full price rather than the GB they were quoted. Rounding the rate
    // moves the credited bytes by well under a kilobyte on any real purchase,
    // and the same stamped rate is what a later refund claws back at, so the
    // two stay consistent.
    const coupon = await resolveOptionalCoupon({
      code: data.couponCode,
      organizationId,
      target: 'topup',
      planSlug: plan.slug,
      // A top-up is not billed by time — one purchase, one application.
      perPeriodMicroUsd: priceMicros,
    })
    const netMicros = coupon ? coupon.netMicroUsd : priceMicros
    // Clamped to 1: a rate of 0 makes `bytesForPayment` throw at settlement,
    // after the money has been taken. Only reachable on an absurd
    // (huge-cap plan, near-total discount) pair, which is exactly the sort of
    // input that should cost the customer nothing rather than break the credit.
    const chargedRate = coupon ? Math.max(1, Math.round(netMicros / gb)) : rate

    const origin = appOrigin()
    const checkout = await gateway.createTopUpCheckout({
      organizationId,
      email,
      gb,
      amountMicroUsd: netMicros,
      pricePerGbMicroUsd: chargedRate,
      coupon,
      successUrl: `${origin}/billing?purchase=success`,
      cancelUrl: `${origin}/billing?purchase=cancelled`,
    })
    emitBusinessEvent('add_money.initiated', {
      org_id: organizationId,
      gb,
      amount_micro_usd: netMicros,
      coupon_code: coupon?.code,
    })
    return checkout
  })

// ---------------------------------------------------------------------------
// Subscription plan lifecycle
// ---------------------------------------------------------------------------

/**
 * Refuse to start a plan when the org already has one.
 *
 * Shared by the card and crypto checkouts on purpose: two copies of this would
 * drift, and the failure mode of a drifted copy is an org paying twice for
 * overlapping plans on two different rails.
 */
async function assertNoLivePlan(organizationId: string): Promise<void> {
  // Ensure the row exists first so the read is authoritative.
  await db
    .insert(organizationBilling)
    .values({ organizationId })
    .onConflictDoNothing()
  const [existing] = await db
    .select({
      subscriptionRef: organizationBilling.subscriptionRef,
      billingMode: organizationBilling.billingMode,
      accountManaged: organizationBilling.accountManaged,
      planPeriodEnd: organizationBilling.planPeriodEnd,
      subscriptionStatus: organizationBilling.subscriptionStatus,
    })
    .from(organizationBilling)
    .where(eq(organizationBilling.organizationId, organizationId))
    .limit(1)
  if (
    existing &&
    blocksNewPlanPurchase(
      {
        subscriptionRef: existing.subscriptionRef,
        billingMode: existing.billingMode,
        accountManaged: existing.accountManaged,
        subscriptionStatus: existing.subscriptionStatus,
        planPeriodEnd: existing.planPeriodEnd,
      },
      new Date(),
    )
  ) {
    throw new PublicError(
      existing.accountManaged
        ? 'This organization is managed by your account team. Contact them to make changes.'
        : existing.billingMode === 'prepaid'
          ? 'This organization already has a plan, manage it from the billing page.'
          : 'This organization already has a plan, manage it from the billing portal.',
    )
  }
}

/**
 * Start a hosted checkout that subscribes the active org to a monthly plan, and
 * return its URL. Rejects if the org already carries a live subscription — plan
 * changes go through the gateway's management portal. The included data is
 * granted to the org's PLAN bucket when the gateway's webhook reports the first
 * payment; this fn never touches the wallet.
 */
export const createSubscriptionCheckout = createServerFn({ method: 'POST' })
  .validator(
    (d: { planSlug: string; couponCode?: string | null; trial?: boolean }) =>
      parseInput(
        z.object({
          planSlug: planSlugInput,
          couponCode: couponCodeInput,
          trial: z.boolean().optional().default(false),
        }),
        d,
      ),
  )
  .handler(async ({ data }): Promise<{ url: string }> => {
    // Subscribing is owner/admin-only.
    const { organizationId, userId, email } = await requireOrgAdmin()
    // The free trial is always the trial plan, whatever slug the client sent,
    // and never carries a coupon: the trial IS the discount.
    const planSlug = data.trial ? TRIAL_PLAN_SLUG : data.planSlug
    if (data.trial) {
      const reason = await trialIneligibilityReason({
        organizationId,
        userId,
        email,
        kind: 'card',
      })
      if (reason) throw new PublicError(reason)
    }

    // Resolve against the ORG'S catalog view, not the public one. PlanPicker is
    // fed `getPlansForOrg`, so an org with a private negotiated plan can click
    // Subscribe on a slug the public catalog does not contain (checkout throws
    // "Unknown plan" forever) — or worse, on a slug that ALSO exists publicly,
    // where the public row's price is what gets charged instead of the quoted
    // one. This is the resolution `SubscriptionCheckoutArgs.plan` documents.
    const plan = await getPlanForOrg(organizationId, planSlug)
    if (!plan) throw new PublicError('Unknown plan')

    await assertNoLivePlan(organizationId)

    // The gateway is responsible for the rest of the double-subscribe defence:
    // a first checkout can complete at the gateway before our `subscriptionRef`
    // is written by its webhook, so only the gateway can see that a live
    // subscription already exists. It refuses in that case.
    const coupon = await resolveOptionalCoupon({
      code: data.trial ? null : data.couponCode,
      organizationId,
      target: 'subscription',
      planSlug: plan.slug,
      // One month: Stripe holds the coupon and re-applies it per renewal
      // invoice, so the duration plays out over time rather than here.
      perPeriodMicroUsd: plan.priceMonthlyMicroUsd,
    })

    const origin = appOrigin()
    const checkout = await gatewayFor(
      'subscriptions',
    ).createSubscriptionCheckout({
      organizationId,
      email,
      plan,
      coupon,
      trial: data.trial
        ? {
            days: CARD_TRIAL_DAYS,
            userId,
            email,
            allowanceBytes: TRIAL_ALLOWANCE_BYTES,
          }
        : undefined,
      // `trial=true`, not `trial=1`: the billing route's `validateSearch` only
      // accepts the boolean form, and the router strips what it does not parse.
      successUrl: `${origin}/billing?plan=success${data.trial ? '&trial=true' : ''}`,
      // A cancelled trial checkout returns to the offer card, not the bare
      // plan grid; the route does not auto-forward again from a cancel.
      cancelUrl: data.trial
        ? `${origin}/billing?plan=cancelled&subscribe=${TRIAL_PLAN_SLUG}&trial=true`
        : `${origin}/billing?plan=cancelled`,
    })
    emitBusinessEvent('plan.initiated', {
      org_id: organizationId,
      plan_slug: plan.slug,
      amount_micro_usd: data.trial
        ? 0
        : plan.priceMonthlyMicroUsd - (coupon?.discountMicroUsd ?? 0),
      coupon_code: coupon?.code,
      trial: data.trial,
    })
    return checkout
  })

/**
 * Start a crypto checkout for a FIXED-LENGTH term.
 *
 * Separate from `createSubscriptionCheckout` because the two are genuinely
 * different products: a card subscription renews itself, while a crypto term is
 * bought outright for N months and simply ends. That difference is why this
 * takes `months` and why it resolves the gateway BY ID rather than through
 * `gatewayFor('subscriptions')` — crypto declares `subscriptions: false`
 * precisely so a plain "Subscribe" click can never land on a rail that cannot
 * bill month two.
 */
export const createCryptoTermCheckout = createServerFn({ method: 'POST' })
  .validator(
    (d: { planSlug: string; months: number; couponCode?: string | null }) =>
      parseInput(
        z.object({
          planSlug: planSlugInput,
          months: termMonthsInput,
          couponCode: couponCodeInput,
        }),
        d,
      ),
  )
  .handler(async ({ data }): Promise<{ url: string }> => {
    const { organizationId, email } = await requireOrgAdmin()

    const gateway = getGateway('cryptomus')
    // The neutral message, and deliberately the same one an unavailable rail
    // gives elsewhere: a customer-facing error must not reveal which rails
    // exist or why one is quiet.
    if (!gateway.isEnabled() || !gateway.createTermCheckout) {
      throw new PublicError(PURCHASING_DISABLED)
    }

    // The org's own catalog view, so a negotiated plan checks out at ITS price.
    const plan = await getPlanForOrg(organizationId, data.planSlug)
    if (!plan) throw new PublicError('Unknown plan')

    await assertNoLivePlan(organizationId)

    const coupon = await resolveOptionalCoupon({
      code: data.couponCode,
      organizationId,
      target: 'term',
      planSlug: plan.slug,
      // The months are the periods. Passing the whole term as one period is
      // what would make a "first month only" code discount the entire prepay.
      perPeriodMicroUsd: plan.priceMonthlyMicroUsd,
      periods: data.months,
    })

    const origin = appOrigin()
    const checkout = await gateway.createTermCheckout({
      organizationId,
      email,
      plan,
      months: data.months,
      coupon,
      // `via=crypto` so the billing page can say "a few minutes, on-chain"
      // instead of the card path's "a few seconds" — and poll for long enough
      // to actually see it land.
      successUrl: `${origin}/billing?plan=success&via=crypto`,
      cancelUrl: `${origin}/billing?plan=cancelled`,
    })
    emitBusinessEvent('plan.initiated', {
      org_id: organizationId,
      plan_slug: plan.slug,
      amount_micro_usd:
        plan.priceMonthlyMicroUsd * data.months -
        (coupon?.discountMicroUsd ?? 0),
      coupon_code: coupon?.code,
    })
    return checkout
  })

/**
 * Whether a hosted management portal is reachable for this org, and if not, why.
 *
 * ONE predicate, shared by `createBillingPortalSession` and the
 * `canManageBilling` flag, so the button and the server can never disagree —
 * a divergence here renders "Manage subscription" to a paying customer and then
 * throws at them when they click it.
 *
 * Routed by funding source first, so a comped or invoiced org is never sent to a
 * portal it has no account with. But a gateway ACCOUNT outlives the funding
 * source: `expirePlanBucket({ detachSubscription: true })` resets it to 'none'
 * on cancellation, and a data-only buyer never has one set, while both still
 * have a real customer record holding their receipts and saved card. So fall
 * back to the account itself.
 *
 * The account check also cuts the other way: a subscription created operator-side
 * in the Stripe Dashboard stamps a funding source via `invoice.paid` but never a
 * customer id (only checkout writes one), so the rail alone would promise a
 * portal that cannot open.
 */
type PortalGateway = PaymentGateway &
  Required<Pick<PaymentGateway, 'createManagementSession'>>

type ManagementAccess =
  | { ok: true; gateway: PortalGateway }
  | { ok: false; reason: 'disabled' | 'noAccount' }

function managementAccessFor(row: {
  billingMode: BillingMode
  paymentRail: PaymentRail
  stripeCustomerId: string | null
}): ManagementAccess {
  const hasStripeAccount = !!row.stripeCustomerId
  // §2.6: the gateway-hosted portal belongs to a gateway-owned SUBSCRIPTION.
  // Everyone else falls back to the account itself (a churned or data-only org
  // still has receipts and a saved card behind its customer record); prepaid
  // customers manage their plan on OUR /billing page either way.
  const owner =
    (gatewayOwnsSubscription(row)
      ? gatewayForFundingSource(row.paymentRail)
      : null) ?? (hasStripeAccount ? getGateway('stripe') : null)
  if (!owner?.createManagementSession) {
    return { ok: false, reason: 'noAccount' }
  }
  const portal = owner as PortalGateway
  // Stripe is the only rail with hosted accounts today. A second one needs its
  // own "do you hold an account for this org" check here.
  if (portal.id === 'stripe' && !hasStripeAccount) {
    return { ok: false, reason: 'noAccount' }
  }
  if (!portal.isEnabled()) return { ok: false, reason: 'disabled' }
  return { ok: true, gateway: portal }
}

/**
 * Open the management portal of the gateway that owns the org's subscription,
 * so owners/admins can update payment methods, view invoices, or cancel.
 */
export const createBillingPortalSession = createServerFn({
  method: 'POST',
}).handler(async (): Promise<{ url: string }> => {
  // The billing portal (payment methods, cancellation) is owner/admin-only.
  const { organizationId } = await requireOrgAdmin()
  const [row] = await db
    .select({
      billingMode: organizationBilling.billingMode,
      paymentRail: organizationBilling.paymentRail,
      stripeCustomerId: organizationBilling.stripeCustomerId,
      accountManaged: organizationBilling.accountManaged,
    })
    .from(organizationBilling)
    .where(eq(organizationBilling.organizationId, organizationId))
    .limit(1)

  if (row?.accountManaged) {
    throw new PublicError(
      'Billing for this organization is handled by your account team.',
    )
  }

  const access = managementAccessFor({
    billingMode: row?.billingMode ?? 'none',
    paymentRail: row?.paymentRail ?? 'none',
    stripeCustomerId: row?.stripeCustomerId ?? null,
  })
  if (!access.ok) {
    // A kill-switched rail is a DEPLOYMENT state, not a fact about this customer:
    // telling an actively billed org it has "no billing account" is both false and
    // a dead end, since the remedy it advertises cannot unlock anything. The
    // neutral copy also avoids naming the rail, or admitting one exists.
    throw new PublicError(
      access.reason === 'disabled'
        ? PURCHASING_DISABLED
        : 'No billing account yet, subscribe to a plan or add data first.',
    )
  }

  const origin = appOrigin()
  return access.gateway.createManagementSession({
    organizationId,
    returnUrl: `${origin}/billing`,
  })
})

/** Everything the dashboard's Plan card needs about the active org, in one call. */
export interface OrgPlanStatus {
  planSlug: string | null
  /** Resolved from the catalog; null when the org's slug isn't in it. */
  planName: string | null
  /** Catalog included data for the org's plan; null when slug unknown. */
  includedGb: number | null
  /** This cycle's data allowance in bytes (plan included + top-ups − refunds).
   *  SIGNED — may be negative from refund debt (the read-time clamp lives in
   *  `remainingBytes`). */
  allowanceBytes: number
  /** Bytes consumed this cycle: max(0, groupUsed − baseline). */
  usedThisCycleBytes: number
  /** Remaining allowance this cycle: max(0, allowance − usedThisCycle). */
  remainingBytes: number
  /** Expiring cycle allowance left. Measure progress bars against THIS, not the
   *  merged total — a denominator including non-expiring credit reads "fine"
   *  right up to the reset. */
  cycleRemainingBytes: number
  /** Non-expiring credit left. Survives every cycle boundary. */
  creditRemainingBytes: number
  /** How the org pays. The subscription ref itself never reaches the client. */
  /** HOW entitlement renews (§1). 'prepaid' means the billing portal is OUR
   *  /billing page; only a 'subscription' org can have a gateway portal. */
  billingMode: BillingMode
  /** The org's billing is run with a human — suppress self-serve CTAs and
   *  point at the account team. Stored per-org fact, not derived from rail. */
  accountManaged: boolean
  /** Whether a hosted management portal is actually reachable for this org:
   *  its funding source is a gateway, that gateway is live, and it hosts one. */
  canManageBilling: boolean
  /** Whether the org can start a self-serve top-up right now — a live rail that
   *  sells top-ups, and an org that isn't account-managed. */
  canBuyData: boolean
  /** Start of the current cycle, ISO string; null when never subscribed. */
  cycleStartAt: string | null
  /** End of the paid cycle, ISO string; null when no active cycle. */
  planPeriodEnd: string | null
  /** The org's per-CYCLE top-up cap (GB); null when no plan. */
  topUpCapGb: number | null
  /** GB of additional-data top-up still purchasable this cycle; null when no
   *  plan. `topUpCapGb` minus what's already been bought since the last grant. */
  topUpRemainingGb: number | null
  hasSubscription: boolean
  /**
   * Whether an active (paid-through) subscription currently unlocks the
   * network. False for no subscription AND for a lapsed one (dunning /
   * past period end) — the app should gate credential creation and usage on
   * this, not on `hasSubscription` alone.
   */
  entitled: boolean
  /**
   * Last-synced gateway subscription status ('active', 'past_due', …), for
   * DISPLAY only — access is `entitled`, never this string. Null when unknown
   * (no subscription, or a comp/invoiced deal no gateway has heard of).
   */
  subscriptionStatus: string | null
  /** The customer scheduled a cancellation in the portal — still paid through
   *  `planPeriodEnd`, then the plan ends instead of renewing. */
  cancelAtPeriodEnd: boolean
  /** Plan the subscription switches to at period end (a scheduled portal
   *  downgrade), resolved to its display name; null when no change pending. */
  scheduledPlanSlug: string | null
  scheduledPlanName: string | null
  /** The org's low-balance alert threshold as a percent of the plan's included
   *  data: null = platform default (LOW_BALANCE_ALERT_PERCENT_DEFAULT), 0 =
   *  alerts off, 1..100 = custom. Set on the Organization page. */
  lowBalanceAlertPercent: number | null
  /** End of the org's active prepaid term — the date the plan is paid
   *  through. Null when the org has no active term (subscriptions renew
   *  themselves, so they never carry one). */
  paidThroughAt: string | null
  /** The active catalog, for the no-subscription plan picker. */
  plans: PricingPlan[]
  /** A live free trial. The plan card shows trial copy and the picker
   *  underneath; a card trial also offers a cancel action. */
  trialing: boolean
  /** Which trial is live: 'card' (Stripe trial period on a real plan) or
   *  'no_card' (a one-week in-house grant). Null when not trialing. */
  trialKind: TrialKind | null
  /** The org used its free trial and holds no plan now: the gate says the
   *  trial ended rather than inviting them to start. */
  trialEnded: boolean
}

/** Plan + balance status for the active org's billing dashboard. */
export const getOrgPlanStatus = createServerFn({ method: 'GET' }).handler(
  async (): Promise<OrgPlanStatus> => {
    const { organizationId } = await requireOrgMember()
    await db
      .insert(organizationBilling)
      .values({ organizationId })
      .onConflictDoNothing()
    const [row] = await db
      .select({
        planSlug: organizationBilling.planSlug,
        subscriptionRef: organizationBilling.subscriptionRef,
        billingMode: organizationBilling.billingMode,
        paymentRail: organizationBilling.paymentRail,
        accountManaged: organizationBilling.accountManaged,
        planPeriodEnd: organizationBilling.planPeriodEnd,
        stripeCustomerId: organizationBilling.stripeCustomerId,
        cycleStartAt: organizationBilling.cycleStartAt,
        cycleAllowanceBytes: organizationBilling.cycleAllowanceBytes,
        creditBytes: organizationBilling.creditBytes,
        groupBaselineBytes: organizationBilling.groupBaselineBytes,
        groupUsedBytes: organizationBilling.groupUsedBytes,
        subscriptionStatus: organizationBilling.subscriptionStatus,
        cancelAtPeriodEnd: organizationBilling.cancelAtPeriodEnd,
        scheduledPlanSlug: organizationBilling.scheduledPlanSlug,
        lowBalanceAlertPercent: organizationBilling.lowBalanceAlertPercent,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, organizationId))
      .limit(1)

    // Org-aware on BOTH counts. This one change is the root fix for every broken
    // enterprise state: resolving against the public active catalog returned null
    // for a private (negotiated) plan, which rendered as a bare dash on the
    // dashboard, as "Your plan" on /billing, and — worst — as "Subscribe to a
    // plan to purchase additional data" shown to a customer who had already paid.
    const [plans, plan] = await Promise.all([
      getPlansForOrg(organizationId),
      getPlanForOrg(organizationId, row?.planSlug ?? null),
    ])

    // Remaining top-up allowance this cycle (only meaningful with a plan).
    const topUpRemainingGb = plan
      ? Math.max(
          0,
          plan.topUpCapGb -
            (await topUpUsedThisCycleBytes(
              organizationId,
              row?.cycleStartAt ?? null,
            )) /
              BYTES_PER_GB,
        )
      : null

    // Usage/remaining are COMPUTED from the cached cycle fields via the pure
    // fns — NO backend call on this read path (the dashboard reads groupUsedBytes,
    // which metering keeps refreshed).
    const allowance = row?.cycleAllowanceBytes ?? 0
    const groupUsed = row?.groupUsedBytes ?? 0
    const baseline = row?.groupBaselineBytes ?? 0
    const split = splitCycleUsage(
      allowance,
      row?.creditBytes ?? 0,
      groupUsed,
      baseline,
    )

    const [activeTerm] =
      row?.billingMode === 'prepaid'
        ? await db
            .select({ termEndAt: prepaidTerms.termEndAt })
            .from(prepaidTerms)
            .where(
              and(
                eq(prepaidTerms.organizationId, organizationId),
                eq(prepaidTerms.active, true),
              ),
            )
            .limit(1)
        : []

    const billingMode = row?.billingMode ?? 'none'
    const paymentRail = row?.paymentRail ?? 'none'
    const accountManaged = row?.accountManaged ?? false
    const trialing =
      row?.subscriptionStatus === 'trialing' && !!row?.subscriptionRef
    // The no-card trial is stored against the Starter catalog row so the grant
    // path has a plan to price and cap, but the customer never bought Starter:
    // everything they see (tile, plan card, customer API) says "Free trial".
    const noCardTrial = trialing && billingMode !== 'subscription'
    const [usedTrial] = row?.subscriptionRef
      ? []
      : await db
          .select({ id: trialClaims.id })
          .from(trialClaims)
          .where(eq(trialClaims.organizationId, organizationId))
          .limit(1)
    // Same predicate the portal server fn uses, so the button never promises
    // something the click cannot deliver.
    const managementAccess = managementAccessFor({
      billingMode,
      paymentRail,
      stripeCustomerId: row?.stripeCustomerId ?? null,
    })

    return {
      planSlug: noCardTrial ? 'trial' : (row?.planSlug ?? null),
      planName: noCardTrial ? 'Free trial' : (plan?.name ?? null),
      includedGb: noCardTrial
        ? allowance / BYTES_PER_GB
        : (plan?.includedGb ?? null),
      allowanceBytes: allowance,
      usedThisCycleBytes: usedThisCycleBytes(groupUsed, baseline),
      remainingBytes: split.remaining,
      // Kept SEPARATE from the merged total on purpose: the dashboard's progress
      // bar must be measured against the cycle allowance alone, since a
      // denominator that includes a non-expiring balance reads "you're fine"
      // right up to the reset.
      cycleRemainingBytes: split.cycleRemaining,
      creditRemainingBytes: split.creditRemaining,
      cycleStartAt: row?.cycleStartAt?.toISOString() ?? null,
      planPeriodEnd: row?.planPeriodEnd?.toISOString() ?? null,
      paidThroughAt: activeTerm?.termEndAt?.toISOString() ?? null,
      topUpCapGb: plan?.topUpCapGb ?? null,
      topUpRemainingGb,
      hasSubscription: !!row?.subscriptionRef,
      // Split out so the UI never has to reason about the subscription ref
      // (which must NEVER reach the client). The axes say how the org pays;
      // the booleans say what it can actually DO right now — resolved
      // server-side because only the server knows which rails are live.
      billingMode,
      accountManaged,
      // A managed org's billing belongs to the account team; never hand the
      // customer a portal that could cancel or repoint the negotiated plan.
      canManageBilling: managementAccess.ok && !accountManaged,
      canBuyData:
        row?.subscriptionStatus !== 'trialing' &&
        purchaseGatewayForOrg({
          paymentRail,
          accountManaged,
          capability: 'topUps',
        }) != null,
      entitled: row
        ? isEntitled(
            {
              subscriptionRef: row.subscriptionRef,
              planPeriodEnd: row.planPeriodEnd,
            },
            new Date(),
          )
        : false,
      subscriptionStatus: row?.subscriptionStatus ?? null,
      cancelAtPeriodEnd: row?.cancelAtPeriodEnd ?? false,
      scheduledPlanSlug: row?.scheduledPlanSlug ?? null,
      // Resolved from the org's own catalog view so a private plan names
      // correctly; falls back to the slug if the catalog no longer carries it.
      scheduledPlanName: row?.scheduledPlanSlug
        ? ((await getPlanForOrg(organizationId, row.scheduledPlanSlug))?.name ??
          row.scheduledPlanSlug)
        : null,
      lowBalanceAlertPercent: row?.lowBalanceAlertPercent ?? null,
      plans,
      trialing,
      trialKind: trialing
        ? billingMode === 'subscription'
          ? 'card'
          : 'no_card'
        : null,
      trialEnded: !row?.subscriptionRef && !!usedTrial,
    }
  },
)

/**
 * End a free trial now. Cancels the trialing subscription at Stripe (nothing
 * was charged, so nothing to prorate) and expires the trial bucket locally
 * under the same idempotency key the `customer.subscription.deleted` webhook
 * uses, so the webhook's later arrival is a no-op. Refuses on anything that is
 * not a live trial: a paying subscription is cancelled from the portal.
 */
export const cancelTrial = createServerFn({ method: 'POST' }).handler(
  async (): Promise<{ ok: true }> => {
    const { organizationId } = await requireOrgAdmin()
    const [row] = await db
      .select({
        subscriptionRef: organizationBilling.subscriptionRef,
        billingMode: organizationBilling.billingMode,
        paymentRail: organizationBilling.paymentRail,
        subscriptionStatus: organizationBilling.subscriptionStatus,
      })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, organizationId))
      .limit(1)
    if (
      !row?.subscriptionRef ||
      row.billingMode !== 'subscription' ||
      row.subscriptionStatus !== 'trialing'
    ) {
      throw new PublicError('This organization is not on a free trial.')
    }
    const gateway = gatewayForFundingSource(row.paymentRail)
    if (!gateway?.cancelSubscription) {
      throw new PublicError(PURCHASING_DISABLED)
    }
    await gateway.cancelSubscription({
      organizationId,
      subscriptionRef: row.subscriptionRef,
    })
    await expirePlanBucket({
      organizationId,
      externalRef: `plan-expiry:sub-deleted:${row.subscriptionRef}`,
      description: 'Free trial cancelled',
      detachSubscription: true,
    })
    emitBusinessEvent('plan.trial_cancelled', {
      org_id: organizationId,
      gateway: gateway.id,
    })
    return { ok: true }
  },
)

/**
 * Whether the caller can start the free trial on the active org, with the
 * offer's figures for the card. `reason` is the customer sentence when not.
 * Runs the same eligibility check as the checkout.
 */
export const getTrialEligibility = createServerFn({ method: 'GET' }).handler(
  async (): Promise<{ offer: TrialOffer | null; reason: string | null }> => {
    const { organizationId, userId, email } = await requireOrgAdmin()
    const offer = await getTrialOffer()
    if (!offer) {
      return {
        offer: null,
        reason: 'The free trial is not available right now.',
      }
    }
    const reason = await trialIneligibilityReason({
      organizationId,
      userId,
      email,
      kind: offer.kind,
    })
    return { offer, reason }
  },
)

/**
 * Set the active org's low-balance alert threshold, as a PERCENT of the plan's
 * included data. `null` restores the platform default; `0` disables alerts;
 * `1..100` is a custom threshold. Manager-gated (owner/admin) — a plain member
 * must not change a billing setting. Clears `lowBalanceNotifiedAt` so the new
 * threshold is evaluated fresh on the next metering tick rather than staying
 * suppressed by a prior alert.
 */
export const setLowBalanceAlertPercent = createServerFn({ method: 'POST' })
  .validator((d: { percent: number | null }) => {
    if (d.percent === null) return { percent: null }
    const n = Number(d.percent)
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      throw new PublicError('Alert threshold must be a whole number 0–100.')
    }
    return { percent: n }
  })
  .handler(async ({ data }): Promise<{ percent: number | null }> => {
    const { organizationId } = await requireOrgAdmin()
    await db
      .insert(organizationBilling)
      .values({ organizationId })
      .onConflictDoNothing()
    await db
      .update(organizationBilling)
      .set({
        lowBalanceAlertPercent: data.percent,
        lowBalanceNotifiedAt: null,
        updatedAt: new Date(),
      })
      .where(eq(organizationBilling.organizationId, organizationId))
    return { percent: data.percent }
  })

// ---------------------------------------------------------------------------
// Coupons
// ---------------------------------------------------------------------------

/** What a checkout form shows once a code has been accepted. */
export interface CouponPreview {
  code: string
  /** Full price of the purchase being previewed, micro-USD. */
  amountMicroUsd: number
  discountMicroUsd: number
  netMicroUsd: number
  /** "20% off on your first month" — the whole claim, assembled server-side so
   *  the form has no rules of its own to get wrong. */
  summary: string
}

/**
 * Price a coupon against a purchase the customer is about to make, WITHOUT
 * starting a checkout.
 *
 * Runs the identical validation the checkout will run, against a price
 * resolved from the org's own catalog — so a code that previews as applied is
 * a code that will apply, and the figure quoted here is the figure charged.
 * The form never computes a discount itself; a client-side copy of these rules
 * is exactly how a customer ends up quoted one price and charged another.
 */
export const previewCoupon = createServerFn({ method: 'POST' })
  .validator(
    (d: {
      code: string
      target: CouponTarget
      planSlug?: string | null
      months?: number | null
      gb?: number | null
    }) =>
      parseInput(
        z.object({
          code: couponCodeInput,
          target: z.enum(COUPON_TARGETS),
          planSlug: planSlugInput.nullish(),
          months: termMonthsInput.nullish(),
          gb: z.number().int().min(MIN_PURCHASE_GB).max(100_000).nullish(),
        }),
        d,
      ),
  )
  .handler(async ({ data }): Promise<CouponPreview> => {
    // Same gate as the checkouts themselves: pricing a discount is a money
    // operation, and a plain member cannot spend.
    const { organizationId } = await requireOrgAdmin()
    if (!data.code) throw new PublicError("That code isn't valid.")

    const [row] = await db
      .select({ planSlug: organizationBilling.planSlug })
      .from(organizationBilling)
      .where(eq(organizationBilling.organizationId, organizationId))
      .limit(1)

    // The price of one billed period and how many of them this purchase
    // covers, both derived here rather than accepted from the client — the
    // whole point of the server owning this. The split (rather than one total)
    // is what makes a coupon's duration mean the same thing on a prepaid term
    // as on a card plan.
    let perPeriodMicroUsd: number
    let periods = 1
    let planSlug: string | null = null
    if (data.target === 'topup') {
      const plan = await getPlanForOrg(organizationId, row?.planSlug ?? null)
      if (!plan) throw new PublicError('Subscribe to a plan first.')
      planSlug = plan.slug
      perPeriodMicroUsd = priceForGb(
        data.gb ?? MIN_PURCHASE_GB,
        planPerGbMicroUsd(plan),
      )
    } else {
      const plan = await getPlanForOrg(organizationId, data.planSlug ?? null)
      if (!plan) throw new PublicError('Unknown plan')
      planSlug = plan.slug
      perPeriodMicroUsd = plan.priceMonthlyMicroUsd
      if (data.target === 'term') periods = data.months ?? 1
    }

    const coupon = await resolveCouponForCheckout({
      code: data.code,
      organizationId,
      target: data.target,
      planSlug,
      perPeriodMicroUsd,
      periods,
    })
    return {
      code: coupon.code,
      amountMicroUsd: perPeriodMicroUsd * periods,
      discountMicroUsd: coupon.discountMicroUsd,
      netMicroUsd: coupon.netMicroUsd,
      summary: `${describeCouponAmount(coupon.rule)} ${describeCouponDuration(
        coupon.rule,
        data.target,
        periods,
      )}`,
    }
  })

// ---------------------------------------------------------------------------
// Public pricing
// ---------------------------------------------------------------------------

/**
 * The active plan catalog, for public pages (marketing pricing page, home).
 * Deliberately unauthenticated — it's on the pricing page anyway.
 */
export const getPublicPlans = createServerFn({ method: 'GET' }).handler(
  async (): Promise<{ plans: PricingPlan[]; trial: TrialOffer | null }> => {
    const [plans, trial] = await Promise.all([
      getActivePlans(),
      getTrialOffer(),
    ])
    return { plans, trial }
  },
)

// ---------------------------------------------------------------------------
// History (customer-facing, active org)
// ---------------------------------------------------------------------------

/** The columns the customer ledger reads — shared by the page fetch and the
 *  CSV export so the two never drift. */
const CUSTOMER_LEDGER_COLUMNS = {
  id: ledgerEntries.id,
  kind: ledgerEntries.kind,
  amountBytes: ledgerEntries.amountBytes,
  balanceAfterBytes: ledgerEntries.balanceAfterBytes,
  provider: ledgerEntries.provider,
  currency: ledgerEntries.currency,
  paidAmountMicros: ledgerEntries.paidAmountMicros,
  description: ledgerEntries.description,
  periodKey: ledgerEntries.periodKey,
  createdAt: ledgerEntries.createdAt,
} as const

/**
 * WHERE clause for the customer ledger view: the caller's org, never `overage`
 * (internal loss accounting the customer was not charged for), and — when the
 * caller passed a kind filter — only those kinds. Shared by the page fetch and
 * the CSV export.
 */
function customerLedgerWhere(organizationId: string, kinds: LedgerKind[]) {
  return and(
    eq(ledgerEntries.organizationId, organizationId),
    ne(ledgerEntries.kind, 'overage'),
    kinds.length > 0 ? inArray(ledgerEntries.kind, kinds) : undefined,
  )
}

/** The server-side ORDER BY for a normalized sort (secondary key keeps a stable
 *  order when many rows share an amount). */
function ledgerOrderBy(sort: LedgerSort) {
  const col =
    sort.field === 'amountBytes'
      ? ledgerEntries.amountBytes
      : ledgerEntries.createdAt
  const dir = sort.dir === 'asc' ? asc : desc
  return [dir(col), desc(ledgerEntries.createdAt)]
}

/** One page of ledger entries for the caller's active organization, with an
 *  optional kind filter and sort (both coerced from untrusted input). */
export const getOrgLedger = createServerFn({ method: 'GET' })
  .validator(
    (d: {
      limit?: number
      offset?: number
      kinds?: string[]
      sortField?: string
      sortDir?: string
    }) => ({
      ...normalizeLedgerPage(d),
      kinds: normalizeLedgerKinds(d.kinds),
      sort: normalizeLedgerSort(d),
    }),
  )
  .handler(async ({ data }): Promise<LedgerPage> => {
    const { organizationId } = await requireOrgMember()
    const { limit, offset, kinds, sort } = data
    const where = customerLedgerWhere(organizationId, kinds)

    const [rows, [totals], present] = await Promise.all([
      db
        .select(CUSTOMER_LEDGER_COLUMNS)
        .from(ledgerEntries)
        .where(where)
        .orderBy(...ledgerOrderBy(sort))
        .limit(limit)
        .offset(offset),
      db.select({ value: count() }).from(ledgerEntries).where(where),
      // Unfiltered on purpose: the filter menu must keep offering the other
      // kinds while one is selected.
      db
        .selectDistinct({ kind: ledgerEntries.kind })
        .from(ledgerEntries)
        .where(customerLedgerWhere(organizationId, [])),
    ])

    return {
      rows: rows.map((r) => ({ ...r, createdAt: r.createdAt.toISOString() })),
      total: Number(totals?.value ?? 0),
      presentKinds: present.map((p) => p.kind),
    }
  })

/** Rows a single CSV export may contain. Customer ledgers are event-only
 *  (purchases, grants, refunds — never per-tick usage), so this is generous;
 *  the export notes if it was hit rather than silently truncating. */
const LEDGER_EXPORT_MAX_ROWS = 10_000

/**
 * The caller's ledger as a CSV string, honoring the same kind filter + sort as
 * the on-screen table (so "export" matches what they're looking at). Returns
 * the whole filtered history in one shot rather than a page — bounded by
 * `LEDGER_EXPORT_MAX_ROWS`, with `truncated` set if the cap was reached.
 */
export const exportOrgLedger = createServerFn({ method: 'GET' })
  .validator(
    (d: { kinds?: string[]; sortField?: string; sortDir?: string }) => ({
      kinds: normalizeLedgerKinds(d.kinds),
      sort: normalizeLedgerSort(d),
    }),
  )
  .handler(async ({ data }): Promise<{ csv: string; truncated: boolean }> => {
    const { organizationId } = await requireOrgMember()
    const where = customerLedgerWhere(organizationId, data.kinds)
    const rows = await db
      .select(CUSTOMER_LEDGER_COLUMNS)
      .from(ledgerEntries)
      .where(where)
      .orderBy(...ledgerOrderBy(data.sort))
      .limit(LEDGER_EXPORT_MAX_ROWS + 1)
    const truncated = rows.length > LEDGER_EXPORT_MAX_ROWS
    const capped = truncated ? rows.slice(0, LEDGER_EXPORT_MAX_ROWS) : rows
    return {
      csv: ledgerRowsToCsv(
        capped.map((r) => ({ ...r, createdAt: r.createdAt.toISOString() })),
      ),
      truncated,
    }
  })
